import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axiosInstance from '../../../../../shared/services/api/axiosInstance';

const CohortAnalysisTable = ({ teamId, appliedFilters }) => {
  const [cohortData, setCohortData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState(null);

  const styles = {
    primaryBg: "#ffffff",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Use theme accent color for all retention percentages
  const getRetentionColor = () => styles.accentColor;

  // Get background color based on percentage (0% = white/transparent, 100% = #1d0c46)
  const getRetentionBackgroundColor = (percentage) => {
    if (percentage === 0) return 'transparent';
    
    // Convert percentage to opacity (0 to 1)
    const opacity = percentage / 100;
    
    // Convert hex to RGB for #1d0c46
    const hex = styles.accentColor;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    
    // Calculate RGB values with opacity (mix with white)
    const finalR = Math.round(r * opacity + 255 * (1 - opacity));
    const finalG = Math.round(g * opacity + 255 * (1 - opacity));
    const finalB = Math.round(b * opacity + 255 * (1 - opacity));
    
    return `rgb(${finalR}, ${finalG}, ${finalB})`;
  };

  // Get text color based on percentage (white for 75%+, normal for lower)
  const getRetentionTextColor = (percentage) => {
    return percentage >= 75 ? '#ffffff' : styles.textPrimary;
  };

  // Get secondary text color based on percentage (white for 75%+, normal for lower)
  const getRetentionSecondaryTextColor = (percentage) => {
    return percentage >= 75 ? 'rgba(255, 255, 255, 0.8)' : styles.textSecondary;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Fetch cohort data
  const fetchCohortData = useCallback(async (page = 1) => {
    try {
      const dataMode = appliedFilters?.dataMode || 'users';
      const params = {
        team_id: teamId,
        days: 150,
        offset: 0,
        page: page,
        limit: 25,
        dataMode
      };

      // Add website filters for Users mode
      if (dataMode === 'users' && appliedFilters?.websites?.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      // Add contracts filter for Wallets mode
      if (dataMode === 'wallets' && appliedFilters?.contracts?.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id).join(',');
      }

      const response = await axiosInstance.get('/overview/cohort-analysis', { params });
      
      if (response.data?.success) {
        return {
          data: response.data.cohortData || [],
          pagination: response.data.pagination
        };
      } else {
        console.warn('Cohort analysis response not successful:', response.data);
        return { data: [], pagination: null };
      }

    } catch (error) {
      console.error('Error fetching cohort data:', error);
      throw error;
    }
  }, [teamId, appliedFilters]);

  // Load data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await fetchCohortData(currentPage);
      setCohortData(result.data);
      setPagination(result.pagination);
    } catch (error) {
      setError(error.message);
      setCohortData([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetchCohortData]);

  // Load data when dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination?.totalPages || 1)) {
      setCurrentPage(newPage);
    }
  };

  // Cohort Row Component
  const CohortRow = ({ cohort }) => (
    <tr className="border-b hover:bg-gray-50" style={{ borderColor: styles.borderColor }}>
      <td className="py-3 px-4 sticky left-0 bg-white" style={{ borderRight: `1px solid ${styles.borderColor}` }}>
        <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
          {formatDate(cohort.cohortDate)}
        </div>
        <div className="text-xs" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {cohort.cohortSize.toLocaleString()} {(appliedFilters?.dataMode || 'users') === 'wallets' ? 'wallets' : 'users'}
        </div>
      </td>
      {[1,2,3,4,5,6,7].map(day => {
        const percentKey = `day${day}Percent`;
        const countKey = `day${day}Count`;
        const percent = cohort[percentKey] || 0;
        const count = cohort[countKey] || 0;
        const backgroundColor = getRetentionBackgroundColor(percent);
        const textColor = getRetentionTextColor(percent);
        const secondaryTextColor = getRetentionSecondaryTextColor(percent);
        
        return (
          <td 
            key={day} 
            className="py-3 px-4 text-center"
            style={{ 
              backgroundColor: backgroundColor
            }}
          >
            <div className="text-sm" style={{ 
              color: textColor,
              fontFamily: 'Raleway, sans-serif' 
            }}>
              {percent}%
            </div>
            <div className="text-xs" style={{ color: secondaryTextColor, fontFamily: 'Raleway, sans-serif' }}>
              ({count.toLocaleString()})
            </div>
          </td>
        );
      })}
    </tr>
  );

  // Pagination Component
  const Pagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-4 p-4" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="text-xs" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          Showing {((currentPage - 1) * 25) + 1} to {Math.min(currentPage * 25, pagination.totalCount)} of {pagination.totalCount} cohorts
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            className="p-2 border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            style={{ borderColor: styles.borderColor }}
          >
            <ChevronLeft className="w-4 h-4" style={{ color: styles.textPrimary }} />
          </button>
          <span className="text-xs" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
            Page {currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pagination.totalPages}
            className="p-2 border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            style={{ borderColor: styles.borderColor }}
          >
            <ChevronRight className="w-4 h-4" style={{ color: styles.textPrimary }} />
          </button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-4" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <p className="text-xs text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          Error loading cohort data: {error}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="p-4" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        {/* Title Section */}
        <div className="mb-4">
          <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
            Cohort Analysis
          </h3>
          {(appliedFilters?.dataMode || 'users') === 'wallets' && (
            <p className="text-xs mt-1" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Track wallet retention over 7 days from their first transaction with selected contracts
            </p>
          )}
        </div>

        {/* Cohort Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${styles.borderColor}`, backgroundColor: styles.primaryBg }}>
                <th className="py-3 px-4 text-left sticky left-0 bg-white" style={{ 
                  borderRight: `1px solid ${styles.borderColor}`,
                  color: styles.textPrimary,
                  fontFamily: 'Raleway, sans-serif',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  Cohort Date
                </th>
                {[1,2,3,4,5,6,7].map(day => (
                  <th key={day} className="py-3 px-4 text-center" style={{ 
                    color: styles.textPrimary,
                    fontFamily: 'Raleway, sans-serif',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    Day {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                // Loading skeleton
                Array.from({ length: 10 }).map((_, index) => (
                  <tr key={index} style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
                    <td className="py-3 px-4 sticky left-0 bg-white animate-pulse" style={{ borderRight: `1px solid ${styles.borderColor}` }}>
                      <div className="h-3 bg-gray-200 rounded w-24 mb-1"></div>
                      <div className="h-2 bg-gray-200 rounded w-16"></div>
                    </td>
                    {[1,2,3,4,5,6,7].map(day => (
                      <td key={day} className="py-3 px-4 text-center animate-pulse">
                        <div className="h-3 bg-gray-200 rounded w-10 mx-auto mb-1"></div>
                        <div className="h-2 bg-gray-200 rounded w-8 mx-auto"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : cohortData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 px-4 text-center">
                    <p className="text-xs" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                      No cohort data available
                    </p>
                  </td>
                </tr>
              ) : (
                cohortData.map((cohort, index) => (
                  <CohortRow
                    key={`${cohort.cohortDate}-${index}`}
                    cohort={cohort}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination />
      </div>
    </div>
  );
};

export default CohortAnalysisTable;
